/**
 * Runtime host / app-mode detection.
 *
 * Emergent-deployment reality (Feb 2026): only ONE custom domain is
 * allowed per Emergent app, so both the admin app and the B2B portal
 * are co-located on `datacenter.universcosmetic.eu`. The portal lives
 * at `/portal/*` on the same host; the admin lives at everything else.
 *
 * This module previously enforced a two-subdomain boundary
 * (`datacenter.` for admin, `portal.` for portal). That boundary has
 * been removed: on any host we mount BOTH route trees and let
 * react-router pick the matching one. The `PORTAL_LINK` helper below
 * is the single source of truth for "how do I link to the portal"
 * from admin UI — it always returns `/portal` now.
 */

// Primary production host — the single custom domain linked in Emergent.
export const ADMIN_HOST = 'datacenter.universcosmetic.eu';
// Legacy portal host constant kept for backward compatibility with
// callers that still import it. Points at the same host + /portal
// path so nothing breaks if it happens to be used in a link.
export const PORTAL_HOST = 'datacenter.universcosmetic.eu';

// Relative path to the portal root, usable from any host.
export const PORTAL_LINK = '/portal';

export const APP_MODE = {
    ADMIN: 'admin',
    PORTAL: 'portal',
    DEV: 'dev',
};

/**
 * Kept for API compatibility. In the co-located topology we always
 * mount both route trees, so this just returns DEV. Callers that used
 * to branch on the returned mode (e.g. to build a cross-subdomain URL)
 * should switch to `PORTAL_LINK` instead — which now always resolves
 * to the correct relative path.
 */
export function getAppMode() {
    return APP_MODE.DEV;
}

/**
 * Was: redirect users who landed on the wrong subdomain to the correct
 * one. In the co-located topology there is no wrong subdomain — both
 * trees are mounted on the same host — so this is a no-op. Retained as
 * an exported function so `App.js` doesn't need a signature change.
 */
export function enforceHostBoundary() {
    return false;
}
